import React, { useCallback, useEffect, useState } from "react";
import DataTable, { TableColumn } from "react-data-table-component";
import { Button } from "@mui/material";
import { Spinner, Tooltip, OverlayTrigger } from "react-bootstrap";
import { useDispatch, useSelector } from "react-redux";
import { AppDispatch } from "src/redux/store";
import moment from 'moment'
import { formatDateLocal, formatTimeZone } from "src/utils/TimeZoneDateFormat";
import { KTIcon } from "src/marketConnect/helpers";
import { getInventoryAdjustmentListAsync } from "src/redux/actions/omsPageActions";
import CreateEditAdjustmentModal from "./CreateUpdateAdjustment";
import TableLoadingShell from "src/utils/TableLoadingShell";
import StatusButton from "../orders/StatusButton";
import AppDatePickerRange from "../orders/components/AppDatePickerRange";
import { toast } from "react-toastify";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faCalendar } from "@fortawesome/free-solid-svg-icons";
import FilterDropdown from "src/marketConnect/helpers/components/FilterDropdown";

const InventoryAdjustmentListing: React.FC = () => {
    const dispatch = useDispatch<AppDispatch>();
    // pagination + search state
    const [paginationData, setPaginationData] = useState({
        page_num: 1,
        page_limit: 10,
        search: "",
        created_at_min: '',
        created_at_max: '',
        sync_status: '',
        sort_order: '',
        sort_by: '',
    });
    const [searchString, setSearchString] = useState("");
    // modal state
    const [showModal, setShowModal] = useState(false);
    const [editData, setEditData] = useState<any>(null);
    const [showDateRangePicker, setShowDateRangePicker] = useState(false)
    const [selectedEndDate, setSelectedEndDate] = useState('')
    const [selectedStarDate, setSelectedStartDate] = useState('')
    const [filters, setFilters] = useState<Record<string, any>>({});
    const [openFilterKey, setOpenFilterKey] = useState<string | null>(null);
    const statusKey = "sync_status";
    const filterOptions = ["PENDING", "SYNCED", "FAILED"];

    // redux state
    const { adjustmentList, pagination, loading } = useSelector((state: any) => state.inventoryAdjustmentSlice);

    // Fetch API
    const fetchAdjustments = useCallback(() => {
        dispatch(
            getInventoryAdjustmentListAsync({
                page: paginationData.page_num,
                limit: paginationData.page_limit,
                search: paginationData.search,
                sort_by: paginationData.sort_by || "",
                sort_order: paginationData.sort_order || "",
                include: "",
                created_at_min: paginationData.created_at_min || "",
                created_at_max: paginationData.created_at_max || "",
                sync_status: paginationData.sync_status ? paginationData.sync_status.toLowerCase() : "",
            })
        ).unwrap()
            .catch((error) => {
                console.log(error, "adjustment err");
                toast.error(error?.data?.message || "Something went wrong while fetching");
            });
    }, [paginationData]);

    useEffect(() => {
        fetchAdjustments();
    }, [fetchAdjustments]);

    useEffect(() => {
        if (!filters[statusKey]) return;

        setPaginationData(prev => ({
            ...prev,
            sync_status: filters[statusKey],
            page_num: 1,
        }));
    }, [filters]);


    // open create modal
    const handleCreate = () => {
        setEditData(null);
        setShowModal(true);
    };

    // open edit modal
    const handleEdit = (row: any) => {
        setEditData(row);
        setShowModal(true);
    };

    const handleResetFilters = () => {
        setFilters({});
        setSelectedStartDate('');
        setSelectedEndDate('');
        setPaginationData({
            page_num: 1,
            page_limit: 10,
            search: '',
            created_at_min: '',
            created_at_max: '',
            sync_status: '',
            sort_order: '',
            sort_by: '',
        });
    };

    const columns: TableColumn<any>[] = [
        {
            name: "Reference Number",
            selector: (row) => row.reference_number || "-",
            sortable: true,
            minWidth: "180px",
            center: true,
        },
        {
            name: "Reason",
            selector: (row) => row.reason || "-",
            // sortable: true,
            minWidth: "150px",
            center: true,
        },
        {
            name: "Warehouse",
            selector: (row) => row.source_warehouse_data?.api_name || "-",
            minWidth: "150px",
            center: true,
        },
        {
            name: "Description",
            width: "200px",
            center: true,
            cell: (row) => {
                const text = row.description || "-";
                const truncated =
                    text.length > 40 ? text.substring(0, 40) + "..." : text;

                return (
                    <OverlayTrigger
                        placement="top"
                        overlay={<Tooltip>{text}</Tooltip>}
                    >
                        <span
                            style={{
                                cursor: "pointer",
                                whiteSpace: "nowrap",
                                overflow: "hidden",
                                textOverflow: "ellipsis",
                                display: "inline-block",
                                maxWidth: "280px",
                            }}
                        >
                            {truncated}
                        </span>
                    </OverlayTrigger>
                );
            },
        },
        {
            name: (
                <>
                    <span>Status</span>
                    <FilterDropdown
                        id={`filter-${statusKey}`}
                        filters={filters}
                        setFilters={setFilters}
                        openFilterKey={openFilterKey}
                        setOpenFilterKey={setOpenFilterKey}
                        hasActiveFilter={!!paginationData.sync_status}
                        options={[
                            {
                                label: "Filter Status",
                                key: statusKey,
                                values: filterOptions,
                            },
                        ]}
                        formatOptionLabel={(val: string) => {
                            if (val === "PENDING") return "Draft";
                            if (val === "SYNCED") return "Processed";
                            return val.charAt(0) + val.slice(1).toLowerCase();
                        }}
                    />
                </>
            ),
            center: true,
            width: "180px",
            cell: (row) => {
                const status = row.sync_status || row.final_status;
                let customText: string | undefined = undefined;
                if (status === "PENDING") {
                    customText = "Draft";
                } else if (status === "SYNCED") {
                    customText = "Processed";
                }
                return (
                    <StatusButton
                        syncStatus={status}
                        customText={customText}
                        syncMessage={
                            ["ERROR", "ERROR_UNLINKED", "ERROR_NO_MAPPING", "SYNCED_WITH_ERROR"].includes(status)
                                ? row.api_message
                                : ""
                        }
                    />
                );
            },
        },
        {
            name: (
                <div style={{ display: "flex", alignItems: "center", gap: "6px" }}>
                    <span>Created At</span>
                    <OverlayTrigger
                        placement="top"
                        overlay={<Tooltip>{formatTimeZone()}</Tooltip>}
                    >
                        <span style={{ cursor: "pointer", color: "darkgray" }}>
                            <i className="fa fa-info-circle" />
                        </span>
                    </OverlayTrigger>
                </div>
            ),
            center: true,
            width: "180px",
            cell: (row) => (row?.created_at ? formatDateLocal(row.created_at) : "-"),
        },
        {
            name: (
                <div style={{ display: "flex", alignItems: "center", gap: "6px" }}>
                    <span>Updated At</span>
                    <OverlayTrigger
                        placement="top"
                        overlay={<Tooltip>{formatTimeZone()}</Tooltip>}
                    >
                        <span style={{ cursor: "pointer", color: "darkgray" }}>
                            <i className="fa fa-info-circle" />
                        </span>
                    </OverlayTrigger>
                </div>
            ),
            center: true,
            width: "180px",
            cell: (row) => (row?.updated_at ? formatDateLocal(row.updated_at) : "-"),
        },
        {
            name: "Actions",
            maxWidth: "100px",
            cell: (row) => (
                <button
                    className="btn btn-icon btn-bg-light btn-color-primary btn-sm me-3 mt-1"
                    onClick={() => handleEdit(row)}
                // disabled={row?.sync_status === "SYNCED"}
                >
                    {row?.sync_status === 'SYNCED' ? (
                        <KTIcon iconName="eye" className="fs-4" />
                    ) : (
                        <KTIcon iconName="pencil" className="fs-4" />
                    )}
                </button>
            ),
            ignoreRowClick: true,
            button: true,
        },
    ];

    return (
        <>
            <div className="mb-3 d-flex justify-content-between align-items-center">
                <span className="fw-bold text-lg" style={{ fontSize: "16px" }}>
                    Inventory Adjustment
                </span>
            </div>

            <div className="card card-flush">
                <div className="card-header d-flex justify-content-between align-items-center">
                    {/* SEARCH */}
                    <div className="position-relative">
                        {searchString === "" && (
                            <i className="bi bi-arrow-return-left position-absolute top-50 translate-middle-y end-0 me-2 text-secondary"></i>
                        )}
                        <input
                            className="form-control"
                            type="search"
                            placeholder="Search"
                            value={searchString}
                            onChange={(e) => setSearchString(e.target.value)}
                            onKeyDown={(e) => {
                                if (e.key === "Enter") {
                                    setPaginationData((prev) => ({
                                        ...prev,
                                        search: searchString,
                                        page_num: 1,
                                    }));
                                }
                            }}
                        />
                    </div>

                    <div className="d-flex align-items-center gap-2">
                        <div className="mx-md-1 my-1 my-md-0">
                            <button
                                className="btn btn-outline-primary btn-sm date-btn"
                                onClick={() => setShowDateRangePicker(!showDateRangePicker)}
                            >
                                <FontAwesomeIcon icon={faCalendar} className="me-2" />
                                {selectedStarDate && selectedEndDate
                                    ? `${moment(selectedStarDate).format('YYYY/MM/DD')} - ${moment(
                                        selectedEndDate
                                    ).format('YYYY/MM/DD')}`
                                    : "Pick a Date Range"}
                            </button>

                            <AppDatePickerRange
                                isVisible={showDateRangePicker}
                                handleClose={() => setShowDateRangePicker(false)}
                                onPickRange={({ startDate, endDate }) => {
                                    setSelectedStartDate(startDate);
                                    setSelectedEndDate(endDate);
                                    setPaginationData((prev) => ({
                                        ...prev,
                                        created_at_min: moment(startDate).format("YYYY-MM-DD"),
                                        created_at_max: moment(endDate).format("YYYY-MM-DD"),
                                        page_num: 1,
                                    }));
                                }}
                            />
                        </div>

                        <div>
                            <button
                                className="btn btn-sm btn-primary w-100px fw-bold py-2"
                                onClick={handleResetFilters}
                            >
                                Reset Filters
                            </button>
                        </div>
                        {/* <Button
                            variant="contained"
                            className="btn btn-sm btn-primary fw-bold py-2"
                            onClick={handleCreate}
                        >
                            + Add Adjustment
                        </Button> */}
                        <button
                            className="btn btn-sm btn-primary fw-bold py-2"
                            onClick={handleCreate}
                        >
                            + Create New Adjustment
                        </button>
                    </div>
                </div>

                <TableLoadingShell loading={loading}>
                <DataTable
                    fixedHeader
                    persistTableHead
                    data={adjustmentList}
                    columns={columns}
                    pagination
                    paginationServer
                    paginationTotalRows={pagination.count}
                    paginationPerPage={paginationData.page_limit}
                    onChangePage={(page) =>
                        setPaginationData((prev) => ({ ...prev, page_num: page }))
                    }
                    onChangeRowsPerPage={(limit) =>
                        setPaginationData((prev) => ({
                            ...prev,
                            page_limit: limit,
                            page_num: 1,
                        }))
                    }
                    progressPending={false}
                    noDataComponent={<div>No records found</div>}
                    customStyles={{
                        table: { style: { height: "55vh" } },
                        headCells: { style: { fontWeight: "bold", fontSize: "14px" } },
                        cells: {
                            style: {
                                padding: "10px",
                                borderBottom: "1px solid gainsboro",
                            },
                        },
                    }}
                />
                </TableLoadingShell>
            </div>

            {/* CREATE + EDIT MODAL */}
            <CreateEditAdjustmentModal
                show={showModal}
                editData={editData}
                onClose={() => setShowModal(false)}
                onSaved={() => {
                    fetchAdjustments();
                    setShowModal(false);
                    setEditData(null);
                }}
            />
        </>
    );
};

export default InventoryAdjustmentListing;
